import { useState } from "react";
import {
  AlertTriangle,
  Download,
  FileCheck2,
  Link2,
  Mail,
  Pencil,
  Plus,
  Trash2,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection } from "@/composants/ui/champ";
import { Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import {
  aujourdHui,
  dateSemaine,
  euros,
  horodatage,
  NATURES_FRAIS,
  quantiteLisible,
} from "@/lib/libelles";
import type {
  AccuseReception,
  ArARattacher,
  ArAValider,
  ArCommunEnPartie,
  CourrielReleve,
  EcartAr,
  FicheCommandeFournisseur,
  NatureFrais,
  TypeProblemeFournisseur,
} from "@/lib/api";
import { api, urlContenuDocument } from "@/lib/api";
import { messageDe } from "@/lib/chargement";

type Agir = (geste: () => Promise<FicheCommandeFournisseur>) => Promise<boolean>;

/** Ce qu'un écart propose d'ouvrir comme problème fournisseur (ticket #17). */
export type ProblemePropose = {
  type: TypeProblemeFournisseur;
  description: string;
  ligne_commande_fournisseur_id: string | null;
};

/**
 * Les AR d'une commande fournisseur (ticket #17, ADR-0039 et ADR-0041). Le
 * chargé d'affaires enregistre chaque AR, révisions comprises ; la base en
 * déduit la date de l'AR, la date prévue et la confirmation. Les écarts avec
 * la commande se lisent ligne à ligne, en toutes lettres ; aucun ne réécrit
 * la commande : le chargé d'affaires corrige sa ligne, ou ouvre un problème.
 *
 * Ticket #18 : l'AR se dépose aussi en fichier. Il attend alors sa validation,
 * où l'humain saisit ce que l'AR dit — l'extracteur n'est pas branché —, et
 * c'est la validation qui l'enregistre (ADR-0038).
 *
 * Ticket #19 : l'AR arrive aussi par la boîte dédiée (ADR-0040), et son
 * courriel reste lisible. Quand le rapprochement hésite entre plusieurs
 * commandes, l'AR s'offre sur chacune : le chargé d'affaires désigne la bonne,
 * où il attend alors sa validation.
 *
 * Ticket #41 : un même fichier couvre parfois plusieurs commandes du
 * fournisseur, d'un ou de plusieurs chargés d'affaires — un document commun.
 * En le validant, on y ajoute les autres commandes, chacune avec sa part ; qui
 * n'agit pas sur toutes rattache la sienne, et le laisse « en partie
 * rattaché » : il s'offre alors sur les autres commandes du fournisseur.
 */
export function AccusesReception({
  fiche,
  peutAgir,
  peutDeposer,
  voitLesMontants,
  agir,
  onProposerProbleme,
}: {
  fiche: FicheCommandeFournisseur;
  peutAgir: boolean;
  peutDeposer: boolean;
  voitLesMontants: boolean;
  agir: Agir;
  onProposerProbleme: (proposition: ProblemePropose) => void;
}) {
  const [saisie, setSaisie] = useState(false);
  const [depot, setDepot] = useState(false);
  const [enCorrection, setEnCorrection] = useState<string | null>(null);
  const [enRattachement, setEnRattachement] = useState<string | null>(null);
  // Une commande en préparation n'est pas partie, une commande annulée est figée : la base
  // refuserait l'AR, l'écran ne le propose pas.
  const recoitDesAr = fiche.statut !== "BROUILLON" && fiche.statut !== "ANNULEE";

  return (
    <Carte>
      <EnTeteCarte className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Accusés de réception</h2>
        <div className="flex flex-wrap gap-2">
          {peutDeposer && recoitDesAr && !depot ? (
            <Bouton taille="compact" variante="discret" onClick={() => setDepot(true)}>
              <Upload className="h-3.5 w-3.5" aria-hidden />
              Déposer le fichier d'un AR
            </Bouton>
          ) : null}
          {peutAgir && recoitDesAr && !saisie ? (
            <Bouton taille="compact" variante="discret" onClick={() => setSaisie(true)}>
              <FileCheck2 className="h-3.5 w-3.5" aria-hidden />
              Enregistrer un AR
            </Bouton>
          ) : null}
        </div>
      </EnTeteCarte>
      {depot ? (
        <CorpsCarte className="border-b border-filet">
          <DepotAr
            onAnnuler={() => setDepot(false)}
            onDeposer={async (fichier) => {
              if (await agir(() => api.deposerArFournisseur(fiche.id, fichier))) {
                setDepot(false);
              }
            }}
          />
        </CorpsCarte>
      ) : null}
      {fiche.ar_a_rattacher.length > 0 ? <ArsARattacher fiche={fiche} agir={agir} /> : null}
      {fiche.ar_a_valider.length > 0 ? (
        <ArsAValider fiche={fiche} voitLesMontants={voitLesMontants} agir={agir} />
      ) : null}
      {fiche.ar_communs_en_partie.length > 0 ? (
        <ArsCommunsEnPartie fiche={fiche} voitLesMontants={voitLesMontants} agir={agir} />
      ) : null}
      {saisie ? (
        <CorpsCarte className="border-b border-filet">
          <FormulaireAr
            fiche={fiche}
            voitLesMontants={voitLesMontants}
            onAnnuler={() => setSaisie(false)}
            onValider={async (champs) => {
              if (await agir(() => api.enregistrerArFournisseur(fiche.id, champs))) {
                setSaisie(false);
              }
            }}
          />
        </CorpsCarte>
      ) : null}
      {fiche.accuses_reception.length === 0 ? (
        <Vide
          titre="Aucun AR"
          detail={
            fiche.statut === "BROUILLON"
              ? "Une commande en préparation n'est pas partie : elle ne reçoit pas encore d'AR."
              : "Le premier AR du fournisseur confirmera la commande."
          }
        />
      ) : (
        <ul className="divide-y divide-filet">
          {fiche.accuses_reception.map((ar, rang) => (
            <li key={ar.id} className="flex flex-col gap-2 px-4 py-3 text-sm">
              {rang === 1 ? (
                <h3 className="text-sm font-semibold text-encre">
                  Chronologie : les AR précédents
                </h3>
              ) : null}
              {enCorrection === ar.id ? (
                <FormulaireEnTeteAr
                  ar={ar}
                  onAnnuler={() => setEnCorrection(null)}
                  onValider={async (champs) => {
                    if (await agir(() => api.corrigerArFournisseur(fiche.id, ar.id, champs))) {
                      setEnCorrection(null);
                    }
                  }}
                />
              ) : (
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-medium text-encre">
                      AR du {dateSemaine(ar.date_ar)}
                      {ar.courant ? <Etiquette ton="actif">AR courant</Etiquette> : null}
                    </p>
                    <p className="text-encre-douce">
                      {ar.date_annoncee
                        ? `Livraison confirmée le ${dateSemaine(ar.date_annoncee)}`
                        : "Sans date confirmée"}
                      {ar.reference_fournisseur ? ` · réf. ${ar.reference_fournisseur}` : null}
                    </p>
                    {ar.courriel ? (
                      <>
                        <p className="text-encre-douce">Reçu par e-mail, puis validé.</p>
                        <Provenance courriel={ar.courriel} />
                      </>
                    ) : ar.import_ocr_id ? (
                      <p className="text-encre-douce">Lu d'un fichier déposé, puis validé.</p>
                    ) : null}
                    {ar.document_commun ? (
                      <p className="text-encre-douce">
                        Document commun à plusieurs commandes du fournisseur
                        {ar.document_id
                          ? " : le document original est entier, la fiche ne montre que" +
                            " vos lignes."
                          : " : son fichier porte les prix d'achat, il ne s'ouvre qu'avec" +
                            " les montants."}
                      </p>
                    ) : null}
                  </div>
                  {ar.document_id ? (
                    ar.document_commun ? (
                      <LienFichier
                        documentId={ar.document_id}
                        libelle={`Voir le document original de l'AR du ${dateSemaine(ar.date_ar)}`}
                        texte="Voir le document original"
                      />
                    ) : (
                      <LienFichier
                        documentId={ar.document_id}
                        libelle={`Télécharger le fichier de l'AR du ${dateSemaine(ar.date_ar)}`}
                      />
                    )
                  ) : null}
                  {peutAgir && recoitDesAr ? (
                    <Bouton
                      taille="compact"
                      variante="discret"
                      aria-label={`Corriger l'AR du ${dateSemaine(ar.date_ar)}`}
                      onClick={() => setEnCorrection(ar.id)}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                      Corriger
                    </Bouton>
                  ) : null}
                  {ar.rattachement_corrigeable && enRattachement !== ar.id ? (
                    <Bouton
                      taille="compact"
                      variante="discret"
                      aria-label={`Corriger le rattachement de l'AR du ${dateSemaine(ar.date_ar)}`}
                      onClick={() => setEnRattachement(ar.id)}
                    >
                      <Undo2 className="h-3.5 w-3.5" aria-hidden />
                      Corriger le rattachement
                    </Bouton>
                  ) : null}
                </div>
              )}
              {ar.import_ocr_id && enRattachement === ar.id ? (
                <FormulaireRattachement
                  onAnnuler={() => setEnRattachement(null)}
                  onCorriger={async (motif) => {
                    const importId = ar.import_ocr_id as string;
                    const fait = await agir(async () => {
                      await api.corrigerRattachementImportOcr(importId, motif);
                      return api.commandeFournisseur(fiche.id);
                    });
                    if (fait) setEnRattachement(null);
                  }}
                />
              ) : null}
              {ar.frais && ar.frais.length > 0 ? (
                <p className="text-encre-douce">
                  Frais annoncés :{" "}
                  {ar.frais.map((f) => `${f.libelle} ${euros(f.montant_ht)}`).join(", ")}
                </p>
              ) : null}
              {ar.courant ? (
                <Ecarts
                  ar={ar}
                  peutAgir={peutAgir}
                  voitLesMontants={voitLesMontants}
                  onProposerProbleme={onProposerProbleme}
                />
              ) : (
                <p className="text-encre-douce">
                  Remplacé par un AR plus récent : ses écarts ne comptent plus.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

/**
 * `texte` : ce que le lien dit à l'écran. Le fichier d'un document commun se
 * dit « Voir le document original » : il porte aussi les commandes de
 * collègues, que la fiche ne montre pas (ticket #48).
 */
export function LienFichier({
  documentId,
  libelle,
  texte = "Fichier",
}: {
  documentId: string;
  libelle: string;
  texte?: string;
}) {
  return (
    <a
      href={urlContenuDocument(documentId)}
      aria-label={libelle}
      className="inline-flex h-9 items-center gap-1.5 rounded-[6px] border border-filet bg-white px-3 text-sm font-medium text-encre hover:bg-chene-clair/40"
    >
      <Download className="h-4 w-4" aria-hidden />
      {texte}
    </a>
  );
}

function DepotAr({
  onAnnuler,
  onDeposer,
}: {
  onAnnuler: () => void;
  onDeposer: (fichier: File) => Promise<void>;
}) {
  const [fichier, setFichier] = useState<File | null>(null);
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!fichier) return;
        setEnvoi(true);
        await onDeposer(fichier);
        setEnvoi(false);
      }}
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor="depot-ar" className="text-sm font-medium text-encre">
          Fichier de l'AR
          <span aria-hidden className="ml-0.5 text-alerte">
            *
          </span>
        </label>
        <input
          id="depot-ar"
          type="file"
          required
          accept=".pdf,.jpg,.jpeg,.png,.heic,.webp"
          onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
          className="text-sm text-encre file:mr-3 file:rounded-[6px] file:border file:border-filet file:bg-white file:px-3 file:py-2 file:text-sm file:text-encre"
        />
        <p className="text-xs text-encre-douce">
          L'AR attendra sa validation : c'est elle qui l'enregistre sur la commande.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !fichier}>
          {envoi ? "Dépôt en cours…" : "Déposer"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/** Les AR déposés en fichier, qui attendent leur validation (ticket #18). */
function ArsAValider({
  fiche,
  voitLesMontants,
  agir,
}: {
  fiche: FicheCommandeFournisseur;
  voitLesMontants: boolean;
  agir: Agir;
}) {
  const [enValidation, setEnValidation] = useState<string | null>(null);
  const [enRejet, setEnRejet] = useState<string | null>(null);
  const [enRattachement, setEnRattachement] = useState<string | null>(null);
  // Valider ou rejeter rend l'import : la fiche se relit, l'AR y est ou non.
  const trancher = (geste: () => Promise<unknown>) =>
    agir(async () => {
      await geste();
      return api.commandeFournisseur(fiche.id);
    });

  return (
    <CorpsCarte className="flex flex-col gap-3 border-b border-filet">
      <h3 className="text-sm font-semibold text-encre">
        {fiche.ar_a_valider.length === 1
          ? "1 AR déposé attend sa validation"
          : `${fiche.ar_a_valider.length} AR déposés attendent leur validation`}
      </h3>
      <ul className="flex flex-col gap-3">
        {fiche.ar_a_valider.map((depose: ArAValider) => (
          <li
            key={depose.id}
            className="flex flex-col gap-2 rounded-[6px] border border-filet p-3 text-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-encre">
                {depose.courriel
                  ? `Reçu par e-mail le ${horodatage(depose.courriel.recu_le)}, à valider`
                  : `Déposé le ${horodatage(depose.importe_le)}, à valider`}
              </p>
              <div className="flex flex-wrap gap-2">
                {depose.document_id ? (
                  <LienFichier
                    documentId={depose.document_id}
                    libelle={`Télécharger l'AR déposé le ${horodatage(depose.importe_le)}`}
                  />
                ) : null}
                {depose.validable &&
                enValidation !== depose.id &&
                enRejet !== depose.id &&
                enRattachement !== depose.id ? (
                  <>
                    <Bouton taille="compact" onClick={() => setEnValidation(depose.id)}>
                      <FileCheck2 className="h-3.5 w-3.5" aria-hidden />
                      Valider
                    </Bouton>
                    <Bouton
                      taille="compact"
                      variante="discret"
                      onClick={() => setEnRejet(depose.id)}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                      Rejeter
                    </Bouton>
                    {depose.rattachement_corrigeable ? (
                      <Bouton
                        taille="compact"
                        variante="discret"
                        onClick={() => setEnRattachement(depose.id)}
                      >
                        <Undo2 className="h-3.5 w-3.5" aria-hidden />
                        Corriger le rattachement
                      </Bouton>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
            {depose.courriel ? <Provenance courriel={depose.courriel} /> : null}
            {enValidation === depose.id ? (
              <ValidationAr
                fiche={fiche}
                voitLesMontants={voitLesMontants}
                libelle="Valider l'AR"
                onAnnuler={() => setEnValidation(null)}
                onValider={async (champs) => {
                  if (await trancher(() => api.validerImportOcr(depose.id, champs))) {
                    setEnValidation(null);
                  }
                }}
              />
            ) : null}
            {enRattachement === depose.id ? (
              <FormulaireRattachement
                onAnnuler={() => setEnRattachement(null)}
                onCorriger={async (motif) => {
                  if (await trancher(() => api.corrigerRattachementImportOcr(depose.id, motif))) {
                    setEnRattachement(null);
                  }
                }}
              />
            ) : null}
            {enRejet === depose.id ? (
              <FormulaireRejet
                onAnnuler={() => setEnRejet(null)}
                onRejeter={async (motif) => {
                  if (await trancher(() => api.rejeterImportOcr(depose.id, motif))) {
                    setEnRejet(null);
                  }
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </CorpsCarte>
  );
}

/**
 * Ticket #41 : les documents communs du fournisseur, rattachés en partie, qui
 * couvrent peut-être cette commande. Les commandes qui les ont reçus ne se
 * nomment pas ; le fichier s'ouvre si LABGEST y a reconnu cette commande
 * (ticket #48).
 */
function ArsCommunsEnPartie({
  fiche,
  voitLesMontants,
  agir,
}: {
  fiche: FicheCommandeFournisseur;
  voitLesMontants: boolean;
  agir: Agir;
}) {
  const [enValidation, setEnValidation] = useState<string | null>(null);
  return (
    <CorpsCarte className="flex flex-col gap-3 border-b border-filet">
      <h3 className="text-sm font-semibold text-encre">
        {fiche.ar_communs_en_partie.length === 1
          ? "1 AR commun, en partie rattaché, couvre peut-être cette commande"
          : `${fiche.ar_communs_en_partie.length} AR communs, en partie rattachés, couvrent` +
            " peut-être cette commande"}
      </h3>
      <ul className="flex flex-col gap-3">
        {fiche.ar_communs_en_partie.map((commun: ArCommunEnPartie) => (
          <li
            key={commun.id}
            className="flex flex-col gap-2 rounded-[6px] border border-filet p-3 text-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-encre">
                  Reçu le {horodatage(commun.courriel?.recu_le ?? commun.importe_le)}
                  <Etiquette ton="alerte">En partie rattaché</Etiquette>
                </p>
                <p className="text-encre-douce">
                  Déjà rattaché à{" "}
                  {commun.commandes_rattachees === 1
                    ? "une autre commande"
                    : `${commun.commandes_rattachees} autres commandes`}{" "}
                  de ce fournisseur.
                  {commun.document_id
                    ? null
                    : voitLesMontants
                      ? " LABGEST n'y a pas reconnu cette commande : son fichier ne vous est" +
                        " pas ouvert."
                      : " Son fichier porte les prix d'achat : il ne s'ouvre qu'avec les" +
                        " montants."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {commun.document_id ? (
                  <LienFichier
                    documentId={commun.document_id}
                    libelle={
                      "Voir le document original de l'AR commun reçu le " +
                      horodatage(commun.importe_le)
                    }
                    texte="Voir le document original"
                  />
                ) : null}
                {commun.validable && enValidation !== commun.id ? (
                  <Bouton taille="compact" onClick={() => setEnValidation(commun.id)}>
                    <FileCheck2 className="h-3.5 w-3.5" aria-hidden />
                    Rattacher cette commande
                  </Bouton>
                ) : null}
              </div>
            </div>
            {commun.courriel ? <Provenance courriel={commun.courriel} /> : null}
            {enValidation === commun.id ? (
              <ValidationAr
                fiche={fiche}
                voitLesMontants={voitLesMontants}
                libelle="Valider la part de cette commande"
                onAnnuler={() => setEnValidation(null)}
                onValider={async (champs) => {
                  const fait = await agir(async () => {
                    await api.validerImportOcr(commun.id, champs);
                    return api.commandeFournisseur(fiche.id);
                  });
                  if (fait) setEnValidation(null);
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </CorpsCarte>
  );
}

/** Ticket #19 : les AR relevés que le rapprochement n'a pas su placer. */
function ArsARattacher({ fiche, agir }: { fiche: FicheCommandeFournisseur; agir: Agir }) {
  return (
    <CorpsCarte className="flex flex-col gap-3 border-b border-filet">
      <h3 className="text-sm font-semibold text-encre">
        {fiche.ar_a_rattacher.length === 1
          ? "1 AR reçu par e-mail est peut-être celui de cette commande"
          : `${fiche.ar_a_rattacher.length} AR reçus par e-mail sont peut-être ceux de cette commande`}
      </h3>
      <p className="text-sm text-encre-douce">
        LABGEST hésite entre plusieurs commandes. Ouvrez le fichier, puis désignez la bonne :
        l'AR y attendra sa validation.
      </p>
      <ul className="flex flex-col gap-3">
        {fiche.ar_a_rattacher.map((propose: ArARattacher) => (
          <li
            key={propose.id}
            className="flex flex-col gap-2 rounded-[6px] border border-filet p-3 text-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-encre">
                Reçu par e-mail le {horodatage(propose.courriel?.recu_le ?? propose.importe_le)}, à
                rattacher
              </p>
              <div className="flex flex-wrap gap-2">
                {propose.document_id ? (
                  <LienFichier
                    documentId={propose.document_id}
                    libelle={`Télécharger l'AR reçu le ${horodatage(propose.importe_le)}`}
                  />
                ) : null}
                {propose.choisissable ? (
                  <Bouton
                    taille="compact"
                    onClick={() =>
                      agir(async () => {
                        await api.choisirCommandeImportOcr(propose.id, fiche.id);
                        return api.commandeFournisseur(fiche.id);
                      })
                    }
                  >
                    <Link2 className="h-3.5 w-3.5" aria-hidden />
                    C'est l'AR de cette commande
                  </Bouton>
                ) : null}
              </div>
            </div>
            {propose.courriel ? <Provenance courriel={propose.courriel} /> : null}
          </li>
        ))}
      </ul>
    </CorpsCarte>
  );
}

/** L'e-mail d'origine d'un AR relevé : l'expéditeur, l'objet (ticket #19). */
export function Provenance({ courriel }: { courriel: CourrielReleve }) {
  return (
    <p className="flex items-start gap-1.5 text-encre-douce">
      <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">
        De {courriel.expediteur}, objet « {courriel.objet || "sans objet"} »
      </span>
    </p>
  );
}

/**
 * Ticket #64 (décision 68) : l'AR rattaché à la mauvaise commande se corrige, avec un
 * motif. Son fichier retourne dans la file des documents entrants, à rattacher de
 * nouveau ; ses écarts en attente tombent. La base refuse, en nommant l'écart, quand
 * un écart né de cet AR est déjà tranché : le message s'affiche tel quel.
 */
function FormulaireRattachement({
  onAnnuler,
  onCorriger,
}: {
  onAnnuler: () => void;
  onCorriger: (motif: string) => Promise<void>;
}) {
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onCorriger(motif.trim());
        setEnvoi(false);
      }}
    >
      <p className="text-encre-douce">
        Ce n'est pas l'AR de cette commande : son fichier retournera dans la file des documents
        entrants, à rattacher de nouveau, et ses écarts en attente tomberont.
      </p>
      <Champ
        libelle="Motif de la correction"
        required
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !motif.trim()}>
          Détacher de cette commande
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

function FormulaireRejet({
  onAnnuler,
  onRejeter,
}: {
  onAnnuler: () => void;
  onRejeter: (motif: string) => Promise<void>;
}) {
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onRejeter(motif.trim());
        setEnvoi(false);
      }}
    >
      <Champ
        libelle="Motif du rejet"
        required
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !motif.trim()}>
          Rejeter le fichier
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

function Ecarts({
  ar,
  peutAgir,
  voitLesMontants,
  onProposerProbleme,
}: {
  ar: AccuseReception;
  peutAgir: boolean;
  voitLesMontants: boolean;
  onProposerProbleme: (proposition: ProblemePropose) => void;
}) {
  if (ar.lignes.length === 0 && ar.ecarts.length === 0) {
    return <p className="text-encre-douce">Lignes non recopiées : aucun écart à montrer.</p>;
  }
  if (ar.ecarts.length === 0) {
    return (
      <p className="text-encre-douce">
        {voitLesMontants
          ? "Conforme à la commande en références et quantités ; les prix se lisent dans les écarts de prix."
          : "Conforme à la commande en références et quantités."}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <Etiquette ton="alerte">
        {ar.ecarts.length === 1 ? "1 écart avec la commande" : `${ar.ecarts.length} écarts avec la commande`}
      </Etiquette>
      <ul className="flex flex-col gap-1.5">
        {ar.ecarts.map((ecart, rang) => (
          <li
            key={`${ecart.nature}-${ecart.ligne_commande_fournisseur_id ?? ecart.designation}-${rang}`}
            className="flex flex-col gap-1 rounded-[6px] border border-filet px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <span className="min-w-0">
              <span className="font-medium text-encre">{ecart.designation}</span>
              <span className="text-encre-douce"> : {phraseEcart(ecart)}</span>
            </span>
            {peutAgir ? (
              <Bouton
                taille="compact"
                variante="discret"
                aria-label={`Ouvrir un problème : ${ecart.designation}, ${phraseEcart(ecart)}`}
                onClick={() =>
                  onProposerProbleme({
                    type: ecart.nature === "REFERENCE" ? "ERREUR_REFERENCE" : "AUTRE",
                    description: `AR du ${dateSemaine(ar.date_ar)}, ${ecart.designation} : ${phraseEcart(ecart)}.`,
                    ligne_commande_fournisseur_id: ecart.ligne_commande_fournisseur_id,
                  })
                }
              >
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                Ouvrir un problème
              </Bouton>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

const nombre = (valeur: string | null) => (valeur === null ? null : Number(valeur));

/** Un écart dit en toutes lettres (ADR-0024) : la couleur ne porte rien seule. */
export function phraseEcart(ecart: EcartAr) {
  switch (ecart.nature) {
    case "REFERENCE":
      return ecart.commande
        ? `référence ${ecart.ar} au lieu de ${ecart.commande}`
        : `référence ${ecart.ar}, la commande n'en porte pas`;
    case "QUANTITE":
      return `quantité ${quantiteLisible(Number(ecart.ar))} au lieu de ${quantiteLisible(Number(ecart.commande))}`;
    case "AJOUT":
      return "ajoutée par le fournisseur, absente de la commande";
    case "ABSENCE":
      return `absente de l'AR (${quantiteLisible(Number(ecart.commande))} commandés)`;
    case "FRAIS_AJOUTES":
      return `frais de ${euros(nombre(ecart.ar))} que la commande ne porte pas`;
  }
}

type FraisSaisis = {
  cle: string;
  frais_commande_fournisseur_id: string | null;
  nature: NatureFrais;
  libelle: string;
  montant_ht: string;
};

type LigneSaisie = {
  cle: string;
  ligne_commande_fournisseur_id: string | null;
  designation: string;
  reference: string;
  quantite: string;
  prix_unitaire_ht: string;
};

/**
 * Ce que l'AR dit d'une commande : sa date annoncée, sa référence, et, si on
 * les recopie, ses lignes et ses frais. Un document commun (ticket #41) en a
 * une par commande qu'il couvre.
 */
type PartSaisie = {
  fiche: FicheCommandeFournisseur;
  dateAnnoncee: string;
  reference: string;
  detail: boolean;
  lignes: LigneSaisie[];
  frais: FraisSaisis[];
  ajouts: number;
};

function partInitiale(fiche: FicheCommandeFournisseur): PartSaisie {
  return {
    fiche,
    dateAnnoncee: "",
    reference: "",
    detail: false,
    // Chaque ligne de la commande, préremplie : on ne retouche que ce que l'AR
    // dit autrement.
    lignes: fiche.lignes.map((ligne) => ({
      cle: ligne.id,
      ligne_commande_fournisseur_id: ligne.id,
      designation: ligne.designation,
      reference: ligne.reference ?? "",
      quantite: String(ligne.quantite_commandee),
      prix_unitaire_ht: ligne.prix_unitaire_ht != null ? String(ligne.prix_unitaire_ht) : "",
    })),
    // Ticket #39 : les frais de la commande, préremplis ; un montant autre que
    // celui retenu devient un écart de prix. Des montants : avec la permission.
    frais: (fiche.frais ?? []).map((porte) => ({
      cle: porte.id,
      frais_commande_fournisseur_id: porte.id,
      nature: porte.nature as NatureFrais,
      libelle: porte.libelle,
      montant_ht: String(porte.montant_retenu_ht ?? porte.montant_ht),
    })),
    ajouts: 0,
  };
}

function partEnvoyee(part: PartSaisie, voitLesMontants: boolean) {
  return {
    date_annoncee: part.dateAnnoncee || null,
    reference_fournisseur: part.reference.trim() || null,
    lignes: part.detail
      ? part.lignes.map((ligne) => ({
          ligne_commande_fournisseur_id: ligne.ligne_commande_fournisseur_id,
          designation: ligne.designation.trim(),
          reference: ligne.reference.trim() || null,
          quantite: ligne.quantite.replace(",", ".") || null,
          // Sans la permission de voir les montants, le prix n'est ni montré ni écrit.
          ...(voitLesMontants
            ? { prix_unitaire_ht: ligne.prix_unitaire_ht.replace(",", ".") || null }
            : {}),
        }))
      : [],
    frais:
      part.detail && voitLesMontants
        ? part.frais.map((annonce) => ({
            frais_commande_fournisseur_id: annonce.frais_commande_fournisseur_id,
            nature: annonce.nature,
            libelle: annonce.libelle.trim(),
            montant_ht: annonce.montant_ht.replace(",", "."),
          }))
        : [],
  };
}

/** La saisie d'un AR à la main (ticket #17) : une date, et la part de la commande. */
function FormulaireAr({
  fiche,
  voitLesMontants,
  onAnnuler,
  onValider,
}: {
  fiche: FicheCommandeFournisseur;
  voitLesMontants: boolean;
  onAnnuler: () => void;
  onValider: (champs: Record<string, unknown>) => Promise<void>;
}) {
  const [dateAr, setDateAr] = useState(aujourdHui());
  const [part, setPart] = useState<PartSaisie>(() => partInitiale(fiche));
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider({ date_ar: dateAr, ...partEnvoyee(part, voitLesMontants) });
        setEnvoi(false);
      }}
    >
      <Champ
        libelle="Date de l'AR"
        type="date"
        required
        value={dateAr}
        onChange={(e) => setDateAr(e.target.value)}
      />
      <SaisiePart part={part} onChange={setPart} voitLesMontants={voitLesMontants} />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !dateAr}>
          Enregistrer l'AR
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

function libelleCommande(fiche: {
  date_commande: string;
  reference_fournisseur: string | null;
  numeros: string[];
}) {
  const numeros = fiche.numeros.join(", ");
  return `${numeros ? `${numeros}, ` : ""}commande du ${dateSemaine(fiche.date_commande)}${
    fiche.reference_fournisseur ? ` (réf. ${fiche.reference_fournisseur})` : ""
  }`;
}

const libelleFiche = (fiche: FicheCommandeFournisseur) =>
  libelleCommande({
    date_commande: fiche.date_commande,
    reference_fournisseur: fiche.reference_fournisseur,
    numeros: fiche.commandes_clients.map((c) => c.numero_commande_client),
  });

type FraisCommunsSaisis = { cle: string; nature: NatureFrais; libelle: string; montant_ht: string };

/**
 * Valider un AR déposé en fichier (ticket #18), qui peut être un document
 * commun (ticket #41) : on y ajoute les autres commandes du même fournisseur
 * qu'il couvre, chacune avec sa part — une ligne qui couvre deux commandes se
 * découpe, chacune recopiant sa quantité. On dit s'il en couvre d'autres encore,
 * qu'on ne rattache pas : il reste alors « en partie rattaché ». Ses frais
 * communs, la base les ventile entre ses commandes, une fois toutes connues.
 */
function ValidationAr({
  fiche,
  voitLesMontants,
  libelle,
  onAnnuler,
  onValider,
}: {
  fiche: FicheCommandeFournisseur;
  voitLesMontants: boolean;
  libelle: string;
  onAnnuler: () => void;
  onValider: (champs: Record<string, unknown>) => Promise<void>;
}) {
  const [dateAr, setDateAr] = useState(aujourdHui());
  const [parts, setParts] = useState<PartSaisie[]>(() => [partInitiale(fiche)]);
  const [ajout, setAjout] = useState("");
  const [chargement, setChargement] = useState(false);
  const [erreurAjout, setErreurAjout] = useState<string | null>(null);
  const [reste, setReste] = useState(false);
  const [fraisCommuns, setFraisCommuns] = useState<FraisCommunsSaisis[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const ajoutables = fiche.commandes_attendant_un_ar.filter(
    (autre) => !parts.some((part) => part.fiche.id === autre.id),
  );
  const poserPart = (rang: number, part: PartSaisie) =>
    setParts(parts.map((p, i) => (i === rang ? part : p)));
  const poserFraisCommuns = (cle: string, champ: keyof FraisCommunsSaisis, valeur: string) =>
    setFraisCommuns(fraisCommuns.map((f) => (f.cle === cle ? { ...f, [champ]: valeur } : f)));

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider({
          date_ar: dateAr,
          parts: parts.map((part) => ({
            commande_fournisseur_id: part.fiche.id,
            ...partEnvoyee(part, voitLesMontants),
          })),
          reste_a_rattacher: reste,
          frais_communs: reste
            ? []
            : fraisCommuns.map((f) => ({
                nature: f.nature,
                libelle: f.libelle.trim(),
                montant_ht: f.montant_ht.replace(",", "."),
              })),
        });
        setEnvoi(false);
      }}
    >
      <Champ
        libelle="Date de l'AR"
        type="date"
        required
        value={dateAr}
        onChange={(e) => setDateAr(e.target.value)}
      />
      {parts.map((part, rang) => (
        <fieldset
          key={part.fiche.id}
          className="flex flex-col gap-3 rounded-[6px] border border-filet p-3"
          aria-label={`Part de la ${libelleFiche(part.fiche)}`}
        >
          <legend className="px-1 text-sm font-semibold text-encre">
            {rang === 0 ? "Cette commande" : "Autre commande"} : {libelleFiche(part.fiche)}
          </legend>
          <SaisiePart
            part={part}
            onChange={(nouvelle) => poserPart(rang, nouvelle)}
            voitLesMontants={voitLesMontants}
          />
          {rang > 0 ? (
            <span>
              <Bouton
                type="button"
                taille="compact"
                variante="discret"
                onClick={() => setParts(parts.filter((_, i) => i !== rang))}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Retirer cette commande
              </Bouton>
            </span>
          ) : null}
        </fieldset>
      ))}
      {ajoutables.length > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <ChampSelection
              libelle="Le document couvre aussi une autre commande de ce fournisseur"
              value={ajout}
              options={[
                { valeur: "", libelle: "Choisir une commande qui attend un AR" },
                ...ajoutables.map((autre) => ({
                  valeur: autre.id,
                  libelle: libelleCommande({
                    date_commande: autre.date_commande,
                    reference_fournisseur: autre.reference_fournisseur,
                    numeros: autre.numeros_commandes_clients,
                  }),
                })),
              ]}
              onChange={(e) => setAjout(e.target.value)}
            />
          </div>
          <Bouton
            type="button"
            variante="discret"
            disabled={!ajout || chargement}
            onClick={async () => {
              setChargement(true);
              setErreurAjout(null);
              try {
                const autre = await api.commandeFournisseur(ajout);
                setParts([...parts, partInitiale(autre)]);
                setAjout("");
              } catch (cause) {
                setErreurAjout(messageDe(cause));
              }
              setChargement(false);
            }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Ajouter cette commande
          </Bouton>
        </div>
      ) : null}
      {erreurAjout ? (
        <p role="alert" className="text-sm text-alerte">
          {erreurAjout}
        </p>
      ) : null}
      <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-encre">
        <input
          type="checkbox"
          checked={reste}
          onChange={(e) => setReste(e.target.checked)}
          className="h-4 w-4 shrink-0 accent-laiton"
        />
        <span>
          Le document couvre encore des commandes que je ne rattache pas : il restera en partie
          rattaché, pour qu'un collègue ou le traitement des documents entrants les rattache
        </span>
      </label>
      {voitLesMontants && !reste ? (
        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold text-encre">Frais communs du document</h4>
          <p className="text-xs text-encre-douce">
            Des frais du document entier, un transport groupé par exemple : ils se ventilent entre
            ses commandes au prorata de leur montant HT. Chacune en reçoit sa part, à porter sur
            elle.
          </p>
          {fraisCommuns.map((commun) => (
            <fieldset
              key={commun.cle}
              className="flex flex-col gap-2 rounded-[6px] border border-filet p-3"
            >
              <legend className="px-1 text-sm font-medium text-encre">Frais communs</legend>
              <div className="grid gap-2 sm:grid-cols-3">
                <ChampSelection
                  libelle="Nature des frais communs"
                  value={commun.nature}
                  options={Object.entries(NATURES_FRAIS).map(([valeur, libelle]) => ({
                    valeur,
                    libelle,
                  }))}
                  onChange={(e) => poserFraisCommuns(commun.cle, "nature", e.target.value)}
                />
                <Champ
                  libelle="Libellé des frais communs"
                  required
                  value={commun.libelle}
                  onChange={(e) => poserFraisCommuns(commun.cle, "libelle", e.target.value)}
                />
                <Champ
                  libelle="Montant HT des frais communs (€)"
                  required
                  inputMode="decimal"
                  value={commun.montant_ht}
                  onChange={(e) => poserFraisCommuns(commun.cle, "montant_ht", e.target.value)}
                />
              </div>
              <span>
                <Bouton
                  type="button"
                  taille="compact"
                  variante="discret"
                  onClick={() => setFraisCommuns(fraisCommuns.filter((f) => f.cle !== commun.cle))}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  Retirer ces frais
                </Bouton>
              </span>
            </fieldset>
          ))}
          <span>
            <Bouton
              type="button"
              taille="compact"
              variante="discret"
              onClick={() =>
                setFraisCommuns([
                  ...fraisCommuns,
                  {
                    cle: `commun-${fraisCommuns.length}-${Date.now()}`,
                    nature: "TRANSPORT",
                    libelle: "",
                    montant_ht: "",
                  },
                ])
              }
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Des frais communs à ventiler
            </Bouton>
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !dateAr}>
          {libelle}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/** La part d'une commande : date annoncée, référence, et ses lignes et frais recopiés. */
function SaisiePart({
  part,
  onChange,
  voitLesMontants,
}: {
  part: PartSaisie;
  onChange: (part: PartSaisie) => void;
  voitLesMontants: boolean;
}) {
  const { lignes, frais, ajouts } = part;
  const poser = (cle: string, champ: keyof LigneSaisie, valeur: string) =>
    onChange({
      ...part,
      lignes: lignes.map((l) => (l.cle === cle ? { ...l, [champ]: valeur } : l)),
    });
  const poserFrais = (cle: string, champ: keyof FraisSaisis, valeur: string) =>
    onChange({
      ...part,
      frais: frais.map((f) => (f.cle === cle ? { ...f, [champ]: valeur } : f)),
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Date de livraison confirmée"
          type="date"
          value={part.dateAnnoncee}
          onChange={(e) => onChange({ ...part, dateAnnoncee: e.target.value })}
        />
        <Champ
          libelle="Référence du fournisseur"
          value={part.reference}
          onChange={(e) => onChange({ ...part, reference: e.target.value })}
        />
      </div>
      <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-encre">
        <input
          type="checkbox"
          checked={part.detail}
          onChange={(e) => onChange({ ...part, detail: e.target.checked })}
          className="h-4 w-4 shrink-0 accent-laiton"
        />
        <span>Recopier les lignes de l'AR, pour en voir les écarts</span>
      </label>
      {part.detail ? (
        <div className="flex flex-col gap-3">
          {lignes.map((ligne) => (
            <fieldset
              key={ligne.cle}
              className="flex flex-col gap-2 rounded-[6px] border border-filet p-3"
            >
              <legend className="px-1 text-sm font-medium text-encre">
                {ligne.ligne_commande_fournisseur_id
                  ? ligne.designation
                  : "Article ajouté par le fournisseur"}
              </legend>
              <div className="grid gap-2 sm:grid-cols-4">
                {ligne.ligne_commande_fournisseur_id ? null : (
                  <Champ
                    libelle="Désignation"
                    required
                    value={ligne.designation}
                    onChange={(e) => poser(ligne.cle, "designation", e.target.value)}
                  />
                )}
                <Champ
                  libelle="Référence sur l'AR"
                  value={ligne.reference}
                  onChange={(e) => poser(ligne.cle, "reference", e.target.value)}
                />
                <Champ
                  libelle="Quantité sur l'AR"
                  inputMode="decimal"
                  value={ligne.quantite}
                  onChange={(e) => poser(ligne.cle, "quantite", e.target.value)}
                />
                {voitLesMontants ? (
                  <Champ
                    libelle="Prix unitaire HT sur l'AR (€)"
                    inputMode="decimal"
                    value={ligne.prix_unitaire_ht}
                    onChange={(e) => poser(ligne.cle, "prix_unitaire_ht", e.target.value)}
                  />
                ) : null}
              </div>
              <span>
                <Bouton
                  type="button"
                  taille="compact"
                  variante="discret"
                  aria-label={`Absente de l'AR : ${ligne.designation || "article ajouté"}`}
                  onClick={() =>
                    onChange({ ...part, lignes: lignes.filter((l) => l.cle !== ligne.cle) })
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  Absente de l'AR
                </Bouton>
              </span>
            </fieldset>
          ))}
          <span>
            <Bouton
              type="button"
              taille="compact"
              variante="discret"
              onClick={() =>
                onChange({
                  ...part,
                  lignes: [
                    ...lignes,
                    {
                      cle: `ajout-${ajouts}`,
                      ligne_commande_fournisseur_id: null,
                      designation: "",
                      reference: "",
                      quantite: "",
                      prix_unitaire_ht: "",
                    },
                  ],
                  ajouts: ajouts + 1,
                })
              }
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Un article que la commande n'a pas
            </Bouton>
          </span>
          {voitLesMontants ? (
            <>
              <h4 className="text-sm font-semibold text-encre">Frais sur l'AR</h4>
              {frais.map((annonce) => (
                <fieldset
                  key={annonce.cle}
                  className="flex flex-col gap-2 rounded-[6px] border border-filet p-3"
                >
                  <legend className="px-1 text-sm font-medium text-encre">
                    {annonce.frais_commande_fournisseur_id
                      ? `Frais « ${annonce.libelle} »`
                      : "Frais que la commande ne porte pas"}
                  </legend>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {annonce.frais_commande_fournisseur_id ? null : (
                      <>
                        <ChampSelection
                          libelle="Nature"
                          value={annonce.nature}
                          options={Object.entries(NATURES_FRAIS).map(([valeur, libelle]) => ({
                            valeur,
                            libelle,
                          }))}
                          onChange={(e) => poserFrais(annonce.cle, "nature", e.target.value)}
                        />
                        <Champ
                          libelle="Libellé des frais"
                          required
                          value={annonce.libelle}
                          onChange={(e) => poserFrais(annonce.cle, "libelle", e.target.value)}
                        />
                      </>
                    )}
                    <Champ
                      libelle={`Montant HT sur l'AR (€) : ${annonce.libelle || "frais ajoutés"}`}
                      required
                      inputMode="decimal"
                      value={annonce.montant_ht}
                      onChange={(e) => poserFrais(annonce.cle, "montant_ht", e.target.value)}
                    />
                  </div>
                  <span>
                    <Bouton
                      type="button"
                      taille="compact"
                      variante="discret"
                      onClick={() =>
                        onChange({ ...part, frais: frais.filter((f) => f.cle !== annonce.cle) })
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      Absents de l'AR
                    </Bouton>
                  </span>
                </fieldset>
              ))}
              <span>
                <Bouton
                  type="button"
                  taille="compact"
                  variante="discret"
                  onClick={() =>
                    onChange({
                      ...part,
                      frais: [
                        ...frais,
                        {
                          cle: `ajout-frais-${ajouts}`,
                          frais_commande_fournisseur_id: null,
                          nature: "TRANSPORT",
                          libelle: "",
                          montant_ht: "",
                        },
                      ],
                      ajouts: ajouts + 1,
                    })
                  }
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  Des frais que la commande ne porte pas
                </Bouton>
              </span>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FormulaireEnTeteAr({
  ar,
  onAnnuler,
  onValider,
}: {
  ar: AccuseReception;
  onAnnuler: () => void;
  onValider: (champs: Record<string, unknown>) => Promise<void>;
}) {
  const [dateAr, setDateAr] = useState(ar.date_ar);
  const [dateAnnoncee, setDateAnnoncee] = useState(ar.date_annoncee ?? "");
  const [reference, setReference] = useState(ar.reference_fournisseur ?? "");
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider({
          date_ar: dateAr,
          date_annoncee: dateAnnoncee || null,
          reference_fournisseur: reference.trim() || null,
        });
        setEnvoi(false);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Champ
          libelle="Date de l'AR"
          type="date"
          required
          value={dateAr}
          onChange={(e) => setDateAr(e.target.value)}
        />
        <Champ
          libelle="Date de livraison confirmée"
          type="date"
          value={dateAnnoncee}
          onChange={(e) => setDateAnnoncee(e.target.value)}
        />
        <Champ
          libelle="Référence du fournisseur"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !dateAr}>
          Enregistrer la correction
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
